import React, { useCallback } from 'react';
import { usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { Flex, Box, xcss } from '@atlaskit/primitives';
import {
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	CUSTOMER_SERVICE_PROJECT,
} from '@atlassian/jira-common-constants';
import { ROUTE_GROUPS, ROUTE_NAMES } from '@atlassian/jira-common-constants/src/spa-routes';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useLayoutStoreActions } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/store';
import { sidebarNav4UiStateResource } from '@atlassian/jira-navigation-apps-resources';
import {
	NavigateBackProvider,
	useNavigateBack,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-back-button';
import {
	AsyncBoardSettingsSidebar,
	BOARD_SETTINGS_ROUTE_NAMES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-board-settings';
import AsyncBusinessCompanyManagedProjectSettingsSidebar from '@atlassian/jira-navigation-apps-sidebar-nav4-business-company-managed-project-settings';
import AsyncBusinessTeamManagedProjectSettingsSidebar from '@atlassian/jira-navigation-apps-sidebar-nav4-business-team-managed-project-settings';
import { SidebarContainer } from '@atlassian/jira-navigation-apps-sidebar-nav4-common/src/ui/sidebar-container';
import {
	AsyncGlobalAppsSettingsSidebar,
	APPS_SETTINGS_ROUTE_NAMES,
	useGlobalAppsSettingsConnectItemActive,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-apps-settings';
import {
	AsyncGlobalIssuesSettingsSidebar,
	ISSUES_SETTINGS_ROUTE_NAMES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-issues-settings';
import {
	AsyncGlobalProductsSettingsSidebar,
	PRODUCTS_SETTINGS_OPERATIONS_ROUTE_NAMES,
	PRODUCTS_SETTINGS_ROUTE_NAMES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-products-settings';
import {
	AsyncGlobalProjectsSettingsSidebar,
	PROJECTS_SETTINGS_ROUTE_NAMES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-projects-settings';
import {
	AsyncGlobalSystemSettingsSidebar,
	SYSTEM_SETTINGS_ROUTE_NAMES,
	useGlobalSystemSettingsConnectItemActive,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-system-settings';
import AsyncHelpCenterSettingsSidebar from '@atlassian/jira-navigation-apps-sidebar-nav4-help-center-settings';
import { AsyncJsmCmpProjectSettingsSidebar } from '@atlassian/jira-navigation-apps-sidebar-nav4-jsm-cmp-project-settings';
import { AsyncJsmTmpProjectSettingsSidebar } from '@atlassian/jira-navigation-apps-sidebar-nav4-jsm-tmp-project-settings';
import AsyncPersonalSettingsSidebar from '@atlassian/jira-navigation-apps-sidebar-nav4-personal-settings';
import {
	AsyncPlanSettingsSidebar,
	PLAN_SETTINGS_ROUTE_NAMES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-plan-settings';
import { Skeleton as ProjectSettingsSkeleton } from '@atlassian/jira-navigation-apps-sidebar-nav4-project-settings-common/src/common/ui/skeleton';
import { PROJECT_SETTINGS_ROUTE_NAMES } from '@atlassian/jira-navigation-apps-sidebar-nav4-project-settings-common/src/constants';
import { useIsConnectAppActive as useIsProjectSettingsConnectAppActive } from '@atlassian/jira-navigation-apps-sidebar-nav4-project-settings-common/src/controllers/app-items';
import { AsyncJpdProjectSettingsSidebar } from '@atlassian/jira-navigation-apps-sidebar-nav4-project-settings-jpd-settings';
import AsyncSidebarContent from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebar-content/src/async';
import AsyncSoftwareCompanyManagedProjectSettingsSidebar from '@atlassian/jira-navigation-apps-sidebar-nav4-software-company-managed-project-settings';
import AsyncSoftwareTeamManagedProjectSettingsSidebar from '@atlassian/jira-navigation-apps-sidebar-nav4-software-team-managed-project-settings';
import { PanelSplitter, SideNav } from '@atlassian/jira-navigation-system/src/v4';
import Placeholder from '@atlassian/jira-placeholder';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { useRelayResource } from '@atlassian/jira-relay-utils';
import type { srcSidebarSelectorQuery } from '@atlassian/jira-relay/src/__generated__/srcSidebarSelectorQuery.graphql';
import { useRouter } from '@atlassian/jira-router';
import { SidebarFeedbackButton } from '@atlassian/jira-sidebar-nav4-feedback-button';
import { BOARD_SETTINGS_CONFIG_QUERY_PARAM_VALUES } from '@atlassian/jira-software-board-settings-rapidboard-config-common/src/common/constants';
import { messages } from './messages';

export const SidebarSelector = () => {
	const { queryReference } = useRelayResource(sidebarNav4UiStateResource);

	return (
		<Placeholder name="sidebar">
			{queryReference ? <SidebarSelectorInternal queryReference={queryReference} /> : null}
		</Placeholder>
	);
};

const ProjectSettingsSidebarSelector = () => {
	const { data: project, loading, error } = useProjectContext();

	if (loading) {
		return (
			<SidebarContainer>
				<ProjectSettingsSkeleton />
			</SidebarContainer>
		);
	}

	if (project?.projectType === CORE_PROJECT) {
		return project.simplified ? (
			<AsyncBusinessTeamManagedProjectSettingsSidebar project={project} />
		) : (
			<AsyncBusinessCompanyManagedProjectSettingsSidebar project={project} />
		);
	}

	if (project?.projectType === SOFTWARE_PROJECT) {
		return project.simplified ? (
			<AsyncSoftwareTeamManagedProjectSettingsSidebar project={project} />
		) : (
			<AsyncSoftwareCompanyManagedProjectSettingsSidebar project={project} />
		);
	}

	if (project?.projectType === SERVICE_DESK_PROJECT) {
		return project.simplified ? (
			<AsyncJsmTmpProjectSettingsSidebar project={project} />
		) : (
			<AsyncJsmCmpProjectSettingsSidebar project={project} />
		);
	}

	if (project?.projectType === PRODUCT_DISCOVERY_PROJECT) {
		return <AsyncJpdProjectSettingsSidebar project={project} />;
	}

	if (project?.projectType === CUSTOMER_SERVICE_PROJECT) {
		return project.simplified ? (
			<AsyncJsmTmpProjectSettingsSidebar project={project} />
		) : (
			<AsyncJsmCmpProjectSettingsSidebar project={project} />
		);
	}

	if (error) {
		throw error;
	}

	throw new Error(
		`No project settings sidebar found for the project type: ${project?.projectType}`,
	);
};

const InternalSidebarSelector = () => {
	const [
		{
			match: { params },
			route,
			location,
			query,
		},
	] = useRouter();
	const { saveCurrentRoute } = useNavigateBack();
	const { isActive: isGlobalAppsSettingsConnectItemActive } =
		useGlobalAppsSettingsConnectItemActive();
	const { isActive: isGlobalSystemSettingsConnectItemActive } =
		useGlobalSystemSettingsConnectItemActive();
	const { isActive: isProjectSettingsConnectAppActive } = useIsProjectSettingsConnectAppActive();

	// Project settings sidebars
	if (
		PROJECT_SETTINGS_ROUTE_NAMES.has(route.name) ||
		route.group === ROUTE_GROUPS.PROJECT_SETTINGS_SOFTWARE ||
		route.group === ROUTE_GROUPS.PROJECT_SETTINGS_CORE ||
		route.group === ROUTE_GROUPS.PROJECT_SETTINGS_SERVICEDESK ||
		(route.name === ROUTE_NAMES.POLARIS_IDEAS && params.section === 'settings') ||
		isProjectSettingsConnectAppActive
	) {
		return <ProjectSettingsSidebarSelector />;
	}

	// Board settings sidebar
	if (
		BOARD_SETTINGS_ROUTE_NAMES.has(route.name) &&
		BOARD_SETTINGS_CONFIG_QUERY_PARAM_VALUES.has(query.config) &&
		fg('jira_nav4_big_board_settings_visual_refresh_m1')
	) {
		return <AsyncBoardSettingsSidebar />;
	}

	// Help center settings sidebar
	if (ff('jcs-master-flag') && route.group === ROUTE_GROUPS.HELP_CENTER_SETTINGS) {
		return <AsyncHelpCenterSettingsSidebar />;
	}

	// Personal settings sidebar
	if (route.group === ROUTE_GROUPS.PERSONAL_SETTINGS) {
		return <AsyncPersonalSettingsSidebar />;
	}

	// Global settings sidebars
	if (PROJECTS_SETTINGS_ROUTE_NAMES.has(route.name)) {
		return <AsyncGlobalProjectsSettingsSidebar />;
	}
	if (ISSUES_SETTINGS_ROUTE_NAMES.has(route.name)) {
		return <AsyncGlobalIssuesSettingsSidebar />;
	}
	if (
		(APPS_SETTINGS_ROUTE_NAMES.has(route.name) || isGlobalAppsSettingsConnectItemActive) &&
		!location.pathname.includes('/discover')
	) {
		return <AsyncGlobalAppsSettingsSidebar />;
	}
	if (PRODUCTS_SETTINGS_ROUTE_NAMES.has(route.name)) {
		return <AsyncGlobalProductsSettingsSidebar />;
	}
	if (
		PRODUCTS_SETTINGS_OPERATIONS_ROUTE_NAMES.has(route.name) &&
		!ff('platform.50cent.operations-general-configurations-sidebar-item_dqyta')
	) {
		return <AsyncGlobalProductsSettingsSidebar />;
	}

	if (SYSTEM_SETTINGS_ROUTE_NAMES.has(route.name) || isGlobalSystemSettingsConnectItemActive) {
		return <AsyncGlobalSystemSettingsSidebar />;
	}

	// Plan settings sidebars
	if (PLAN_SETTINGS_ROUTE_NAMES.has(route.name)) {
		return <AsyncPlanSettingsSidebar />;
	}

	// the route to navigate back to will be whichever route was last displayed
	// with the default sidebar on
	saveCurrentRoute();
	return <AsyncSidebarContent />;
};

/**
 * This test ID is used in pollinator tests. Do not change lightly.
 * It's only added once the component is rendered client-side.
 */
const TEST_ID = 'page-layout.sidebar';

const SidebarSelectorInternal = ({
	queryReference,
}: {
	queryReference: PreloadedQuery<srcSidebarSelectorQuery>;
}) => {
	const { formatMessage } = useIntl();

	const data = usePreloadedQuery<srcSidebarSelectorQuery>(
		graphql`
			query srcSidebarSelectorQuery($cloudId: ID!) {
				jira @required(action: THROW) {
					navigationUIState(cloudId: $cloudId)
						@required(action: THROW)
						@optIn(to: "JiraEntityProperty") {
						isLeftSidebarCollapsed @required(action: THROW)
						leftSidebarWidth @required(action: THROW)
					}
				}
			}
		`,
		queryReference,
	);

	const defaultCollapsed = data.jira.navigationUIState.isLeftSidebarCollapsed;
	const defaultWidth = data.jira.navigationUIState.leftSidebarWidth;

	const [, { setLeftSidebarState }] = useLayoutStoreActions();
	const onExpand = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				setLeftSidebarState({ isCollapsed: false });
			}
		},
		[setLeftSidebarState],
	);

	const onCollapse = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				setLeftSidebarState({ isCollapsed: true });
			}
		},
		[setLeftSidebarState],
	);

	const onResizeEnd = useCallback(
		({ finalWidth }: { finalWidth: number }) => {
			setLeftSidebarState({ width: finalWidth });
		},
		[setLeftSidebarState],
	);

	return (
		<SideNav
			testId={__SERVER__ ? undefined : TEST_ID}
			defaultCollapsed={defaultCollapsed}
			defaultWidth={defaultWidth}
			onExpand={onExpand}
			onCollapse={onCollapse}
		>
			<NavigateBackProvider>
				<Flex direction="column" xcss={sidebarContainerStyles} justifyContent="space-between">
					<Box xcss={sidebarContentStyles}>
						<InternalSidebarSelector />
					</Box>
					<SidebarFeedbackButton />
				</Flex>
				<PanelSplitter
					label={formatMessage(messages.sideBarPanelSplitterLabel)}
					onResizeEnd={onResizeEnd}
				/>
			</NavigateBackProvider>
		</SideNav>
	);
};

const sidebarContainerStyles = xcss({
	height: '100%',
});

const sidebarContentStyles = xcss({
	flex: '1',
	minHeight: '0',
	overflowX: 'auto',
});
